import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal, Chapter, EASE } from "./shared";

export const Faq = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="scroll-mt-24 bg-white" data-testid="faq-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <Chapter num="06" label={t.faq.chapter.split("/ ")[1]} />
        <div className="mt-12 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 data-testid="faq-heading" className="font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-neutral-950 sm:text-4xl">
                {t.faq.heading}
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-500">{t.faq.sub}</p>
              <a
                href="https://margtor.africa"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-margtor-link"
                className="group mt-6 inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-paper px-4 py-2 font-mono text-xs font-medium text-neutral-600 transition-[border-color,color,background-color] duration-300 hover:border-brand-600 hover:bg-brand-100 hover:text-brand-700"
              >
                {t.faq.linkLabel}
                <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-7" data-testid="faq-list">
            {t.faq.items.map(([q, a], i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-t border-neutral-200 last:border-b">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    aria-expanded={open === i}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className={`font-display text-lg font-bold transition-colors duration-300 sm:text-xl ${open === i ? "text-brand-700" : "text-neutral-900"}`}>
                      {q}
                    </span>
                    <motion.span
                      animate={{ rotate: open === i ? 45 : 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors duration-300 ${open === i ? "border-brand-600 bg-brand-600 text-white" : "border-neutral-200 text-neutral-500"}`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className="pb-7 pr-4 text-sm leading-relaxed text-neutral-500 sm:pr-12" data-testid={`faq-answer-${i}`}>{a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
